/** `[lat, lng]`, the order Leaflet expects. */
export type LatLng = [number, number];

/** `[[south, west], [north, east]]`, the shape of a Leaflet `LatLngBounds` literal. */
export type LatLngBounds = [[number, number], [number, number]];

/**
 * Reads the outer ring of a WKT `POLYGON ((lon lat, lon lat, ...))` into Leaflet's `[lat, lng]` order.
 * Returns an empty array for anything that is not a polygon with at least three vertices.
 */
export function wktPolygonToLatLngRing(wkt: string | null | undefined): LatLng[] {
  if (!wkt) {
    return [];
  }

  const match = /^\s*POLYGON\s*\(\s*\(([^()]*)\)/i.exec(wkt);
  if (!match) {
    return [];
  }

  const ring: LatLng[] = [];
  for (const pair of match[1].split(',')) {
    const [lon, lat] = pair.trim().split(/\s+/).map(Number);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
      return [];
    }
    ring.push([lat, lon]);
  }

  return ring.length >= 3 ? ring : [];
}

/** The smallest box containing every given point, or `null` when there are none. */
export function boundsOf(points: LatLng[]): LatLngBounds | null {
  if (points.length === 0) {
    return null;
  }

  let south = Infinity;
  let north = -Infinity;
  let west = Infinity;
  let east = -Infinity;

  for (const [lat, lng] of points) {
    south = Math.min(south, lat);
    north = Math.max(north, lat);
    west = Math.min(west, lng);
    east = Math.max(east, lng);
  }

  return [[south, west], [north, east]];
}
